import React, { useEffect, useState, useCallback } from "react";
import {
  getPartnerInvestments,
  deleteInvestment,
  updateInvestment,
} from "../../services/investmentService";
import AddInvestmentModal from "./AddInvestor";

// ─── Formatters ───────────────────────────────────────────────────────────────
const nfPKR = new Intl.NumberFormat("en-PK", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const nfG = new Intl.NumberFormat("en-PK", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("en-PK", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const TYPE_BADGE = {
  gold: { bg: "#fff8e1", color: "#b45309", border: "#fde68a" },
  silver: { bg: "#f0f4ff", color: "#1d4ed8", border: "#bfdbfe" },
  cash: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
};

const CAT_LABELS = {
  pc: "PC",
  lab: "Lab",
  bar: "Bar",
  coin: "Coin",
  ginni: "Ginni",
  silver: "Silver",
};

// ─── Type badge ───────────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const s = TYPE_BADGE[type] ?? {
    bg: "#f3f4f6",
    color: "#374151",
    border: "#d1d5db",
  };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        borderRadius: 6,
        padding: "2px 8px",
      }}
    >
      {type}
    </span>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel, loading }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={onCancel}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
        }}
      />
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 12,
          padding: "1.5rem 2rem",
          maxWidth: 380,
          width: "90%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
        <p
          style={{
            fontSize: 15,
            color: "#333",
            marginBottom: "1.25rem",
            lineHeight: 1.5,
          }}
        >
          {message}
        </p>
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Expandable category detail ───────────────────────────────────────────────
function CategoryDetail({ categories }) {
  return (
    <div
      style={{
        padding: "10px 16px 12px",
        background: "#fafafa",
        borderTop: "1px solid #e5e7eb",
      }}
    >
      {categories.map((cat) => {
        const catTotal =
          cat.totalWeight ??
          cat.rows.reduce((s, r) => s + (r.weightGram ?? 0), 0);
        const catAmount =
          cat.totalAmount ?? cat.rows.reduce((s, r) => s + (r.amount ?? 0), 0);
        return (
          <div key={cat.catKey} className="mb-3">
            {/* category header */}
            <div className="d-flex align-items-center gap-2 mb-1">
              <span style={{ fontWeight: 700, fontSize: 13, color: "#0d6efd" }}>
                {CAT_LABELS[cat.catKey] ?? cat.catKey}
              </span>
              <span
                style={{
                  fontSize: 11,
                  background: "#dbe8ff",
                  color: "#0d6efd",
                  borderRadius: 8,
                  padding: "1px 7px",
                }}
              >
                {nfG.format(catTotal)} g
              </span>
              <span
                style={{
                  fontSize: 11,
                  background: "#d1fae5",
                  color: "#065f46",
                  borderRadius: 8,
                  padding: "1px 7px",
                }}
              >
                Rs. {nfPKR.format(catAmount)}
              </span>
            </div>

            {/* rows table */}
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    {[
                      "Brand",
                      "Qty",
                      "Weight",
                      "Rate (PKR/tola)",
                      "Amount (PKR)",
                      "Note",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                          color: "#555",
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cat.rows.map((row, i) => (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fa" }}
                    >
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                        }}
                      >
                        {row.brandName ?? "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                        }}
                      >
                        {row.qty}
                      </td>
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                        }}
                      >
                        {nfG.format(row.weight)} {row.unit}
                        {row.unit === "tola" && (
                          <span style={{ color: "#888", fontSize: 11 }}>
                            {" "}
                            ({nfG.format(row.weightGram)} g)
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                        }}
                      >
                        {nfPKR.format(row.rate)}
                      </td>
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                          color: "#15803d",
                          fontWeight: 600,
                        }}
                      >
                        Rs. {nfPKR.format(row.amount)}
                      </td>
                      <td
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "5px 8px",
                          textAlign: "center",
                          color: "#888",
                        }}
                      >
                        {row.note || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Single investment row ────────────────────────────────────────────────────
function InvestmentRow({ inv, index, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const isMetal = inv.type === "gold" || inv.type === "silver";
  const hasCats = isMetal && inv.categories?.length > 0;

  return (
    <>
      <tr
        style={{
          background: index % 2 === 0 ? "#fff" : "#f9fafb",
          transition: "background 0.15s",
        }}
      >
        {/* # */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            color: "#888",
            fontSize: 13,
          }}
        >
          {index + 1}
        </td>

        {/* Type */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
          }}
        >
          <TypeBadge type={inv.type} />
        </td>

        {/* Date */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        >
          {fmtDate(inv.dateTime)}
        </td>

        {/* Categories summary or Cash amount */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            fontSize: 12,
          }}
        >
          {inv.type === "cash" ? (
            <span style={{ color: "#15803d", fontWeight: 600 }}>
              PKR {nfPKR.format(inv.cashAmount)}
            </span>
          ) : (
            inv.categories?.map((c) => (
              <span
                key={c.catKey}
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  marginRight: 4,
                  marginBottom: 2,
                  background: "#f0f4ff",
                  color: "#0d6efd",
                  border: "1px solid #bfdbfe",
                  borderRadius: 6,
                  padding: "1px 7px",
                }}
              >
                {CAT_LABELS[c.catKey] ?? c.catKey}
              </span>
            ))
          )}
        </td>

        {/* Total Weight */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            fontSize: 12,
          }}
        >
          {isMetal ? (
            <>
              <strong>{nfG.format(inv.totalWeight ?? 0)}</strong> g
            </>
          ) : (
            "—"
          )}
        </td>

        {/* Total Amount */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            fontSize: 12,
          }}
        >
          <strong style={{ color: "#15803d" }}>
            Rs.{" "}
            {nfPKR.format(
              inv.type === "cash" ? inv.cashAmount : (inv.totalAmount ?? 0),
            )}
          </strong>
        </td>

        {/* Notes */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            fontSize: 12,
            color: "#888",
            maxWidth: 120,
          }}
        >
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {inv.notes || "—"}
          </span>
        </td>

        {/* Actions */}
        <td
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 10px",
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          <div className="d-flex gap-1 justify-content-center">
            {/* expand/collapse — only for metal */}
            {hasCats && (
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 11, padding: "2px 8px" }}
                onClick={() => setExpanded((p) => !p)}
                title={expanded ? "Collapse" : "View details"}
              >
                {expanded ? "▲" : "▼"}
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: 11, padding: "2px 8px" }}
              onClick={() => onEdit(inv)}
              title="Edit"
            >
              ✏
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: 11, padding: "2px 8px" }}
              onClick={() => onDelete(inv)}
              title="Delete"
            >
              🗑
            </button>
          </div>
        </td>
      </tr>

      {/* Expandable detail row */}
      {expanded && hasCats && (
        <tr>
          <td colSpan={8} style={{ padding: 0, border: "1px solid #e5e7eb" }}>
            <CategoryDetail categories={inv.categories} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
const InvestmentHistoryModal = ({
  show,
  onClose,
  partner,
  partners,
  setPartner,
  getPartnerRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [investments, setInvestments] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // investment to delete
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // investment to edit
  const [loadingEditInvestment, setLoadingEditInvestment] = useState(false);

  // ── fetch ─────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!partner?._id) return;
    try {
      setLoading(true);
      const res = await getPartnerInvestments(partner._id);
      if (res.success) setInvestments(res.investments);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [partner]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const refreshEverything = async () => {
    //changin the history parent will automatically refetch the partners investments

    const newPartners = await getPartnerRefresh();
    console.log(newPartners);

    if (newPartners && Array.isArray(newPartners)) {
      const historyPartner = newPartners.find((p) => p._id === partner._id);
      console.log({ newPartners });
      console.log({ historyPartner });
      setPartner(historyPartner || {});
    }

    //history partner should be updated automatically
    //partners should be refreshed
    //inivestments should be refreshed
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      const res = await deleteInvestment(deleteTarget._id);
      console.log({ res });
      if (res.success) {
        await refreshEverything();
        // setInvestments((prev) =>
        //   prev.filter((i) => i._id !== deleteTarget._id),
        // );
        setDeleteTarget(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  // ── after edit save ───────────────────────────────────────────────────────
  const handleEditSave = (updatedInvestment) => {
    if (updatedInvestment) {
      setInvestments((prev) =>
        prev.map((i) =>
          i._id === updatedInvestment._id ? updatedInvestment : i,
        ),
      );
    }
    setEditTarget(null);
    load(); // re-fetch to get fresh populated data
  };

  const handleUpdate = async (id, payload) => {
    setLoadingEditInvestment(true);
    const res = await updateInvestment(id, payload);
    if (res.success) {
      await refreshEverything();
      setLoadingEditInvestment(false);
      return true;
    }
    setLoadingEditInvestment(false);
    return false;
  };
  if (!show) return null;

  // ── summary totals ────────────────────────────────────────────────────────
  const totals = partner?.totals ?? {};

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1040,
        }}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-labelledby="invHistoryTitle"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          background: "#fff",
          borderRadius: 16,
          padding: "1.75rem 2rem",
          width: "min(1100px, 95vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        }}
      >
        {/* Title */}
        <h2
          id="invHistoryTitle"
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.5rem",
            color: "#3d3d3d",
            marginBottom: "1rem",
          }}
        >
          Investment History
        </h2>

        {/* Summary strip */}
        <div
          className="d-flex flex-wrap gap-3 justify-content-center mb-3 p-2 rounded"
          style={{
            background: "#f8f9fa",
            border: "1px solid #e5e7eb",
            fontSize: 13,
          }}
        >
          <span>
            <strong>Partner:</strong> {partner?.name}
          </span>
          <span style={{ color: "#b45309" }}>
            🥇 Gold: <strong>{nfG.format(totals.goldGram ?? 0)} g</strong>
          </span>
          <span style={{ color: "#1d4ed8" }}>
            🥈 Silver: <strong>{nfG.format(totals.silverGram ?? 0)} g</strong>
          </span>
          <span style={{ color: "#15803d" }}>
            💵 Cash: <strong>PKR {nfPKR.format(totals.cashPKR ?? 0)}</strong>
          </span>
          <span style={{ color: "#6d28d9" }}>
            📊 Total: <strong>PKR {nfPKR.format(totals.totalPKR ?? 0)}</strong>
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.875rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f0f0f0" }}>
                {[
                  "#",
                  "Type",
                  "Date / Time",
                  "Categories / Amount",
                  "Total Weight",
                  "Total Amount (PKR)",
                  "Notes",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      border: "1px solid #dee2e6",
                      padding: "9px 10px",
                      textAlign: "center",
                      fontWeight: 600,
                      color: "#444",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#888",
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : investments.length > 0 ? (
                investments.map((inv, idx) => (
                  <InvestmentRow
                    key={inv._id}
                    inv={inv}
                    index={idx}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#888",
                    }}
                  >
                    No investments yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Close */}
        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
          <button
            onClick={onClose}
            style={{
              background: "#7c6fcd",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 32px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.target.style.background = "#6a5fc1")}
            onMouseLeave={(e) => (e.target.style.background = "#7c6fcd")}
          >
            Close
          </button>
        </div>
      </div>

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <ConfirmDialog
          message={`Delete this ${deleteTarget.type} investment from ${fmtDate(deleteTarget.dateTime)}? This will reverse all inventory and partner totals.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}

      {/* ── Edit modal — reuse AddInvestmentModal with prefilled data ── */}
      {editTarget && (
        <AddInvestmentModal
          show={!!editTarget}
          onClose={() => setEditTarget(null)}
          onSave={handleEditSave}
          partners={partners}
          onUpdate={handleUpdate}
          editData={editTarget} // pass existing investment so modal can prefill
          isLoading={loadingEditInvestment}
        />
      )}
    </>
  );
};

export default InvestmentHistoryModal;
